import type {
    AllowedWebsite,
    IpgConfig,
    IpwConfig,
    MerchantConfig,
} from './backend_config.types';
import {
    MerchantWebsiteType,
    MerchantConfigSchema,
    RpaModuleName,
} from './backend_config.types';
import {
    IpgSystemId,
    IpwSystemId,
    IpgSystemIdKey,
    IpwSystemIdKey,
    type ThirdPartySystemId,
} from './types/third-party-system.enums';

export const ipgConfigValorDemo: IpgConfig = {
    ipg_id: IpgSystemId.VALOR_US,
    name: 'Valor',
    urlPattern: 'https://*.valorpaytech.com/*',
    workflow_prefix: 'valor',
    allowedWebsites: [
        {
            type: MerchantWebsiteType.IPG,
            system_id: IpgSystemId.VALOR_US,
            url: 'https://demo.valorpaytech.com',
            name: 'Valor Portal (Demo)',
            allowedUrlPatterns: ['https://*.valorpaytech.com/*'],
            sessionPersistence: 'persistent',
            promoteSessionCookies: false,
            urlPatternsRules: [],
            fetchDefaultAction: 'allow',
            iframeUrlPatterns: [],
            iframeDefaultAction: 'allow',
        },
    ],
    rpaModules: [RpaModuleName.VALORPAYTECH_IPG],

    // Valor specific config
    default_api_url: 'https://securelink-staging.valorpaytech.com:4430',

    // This default channel is for Suede.
    default_channel: '3544e7efd37def83b47c01c82f3241a0',
};

export const ipgConfigValorProd: IpgConfig = {
    ipg_id: IpgSystemId.VALOR_US,
    name: 'Valor',
    urlPattern: 'https://*.valorpaytech.com/*',
    workflow_prefix: 'valor',
    allowedWebsites: [
        {
            type: MerchantWebsiteType.IPG,
            system_id: IpgSystemId.VALOR_US,
            url: 'https://online.valorpaytech.com',
            name: 'Valor Portal',
            allowedUrlPatterns: ['https://*.valorpaytech.com/*'],
            sessionPersistence: 'persistent',
            promoteSessionCookies: false,
            urlPatternsRules: [],
            fetchDefaultAction: 'allow',
            iframeUrlPatterns: [],
            iframeDefaultAction: 'allow',
        },
    ],
    rpaModules: [RpaModuleName.VALORPAYTECH_IPG],

    // Valor specific config
    default_api_url: 'https://securelink.valorpaytech.com',

    // This default channel is for Suede.
    default_channel: '57478182a981f8c43b84e5c651e5fdf4',
};

interface DefaultLaunchWebsiteConfig {
    readonly allowedWebsites: readonly { readonly url: string }[];
    readonly name: string;
}

interface DefaultAllowedWebsiteConfig {
    readonly allowedWebsites: readonly AllowedWebsite[];
    readonly name: string;
}

interface DefaultIntegratedWebsiteNavigationConfig extends DefaultLaunchWebsiteConfig {
    readonly allowedWebsites: readonly {
        readonly allowedUrlPatterns: readonly string[];
        readonly url: string;
    }[];
    readonly urlPattern: string;
}

function requireDefaultLaunchUrl(config: DefaultLaunchWebsiteConfig): string {
    const [allowedWebsite] = config.allowedWebsites;
    if (allowedWebsite === undefined) {
        throw new Error(`${config.name} launch URL is not configured.`);
    }
    return allowedWebsite.url;
}

function requireDefaultAllowedWebsite(
    config: DefaultAllowedWebsiteConfig
): AllowedWebsite {
    const [allowedWebsite] = config.allowedWebsites;
    if (allowedWebsite === undefined) {
        throw new Error(`${config.name} allowed website is not configured.`);
    }
    return allowedWebsite;
}

function requireDefaultNavigationAllowedUrlPatterns(
    config: DefaultIntegratedWebsiteNavigationConfig
): readonly string[] {
    const patterns: Set<string> = new Set([config.urlPattern]);
    for (const allowedWebsite of config.allowedWebsites) {
        for (const allowedUrlPattern of allowedWebsite.allowedUrlPatterns) {
            patterns.add(allowedUrlPattern);
        }
    }
    if (patterns.size === 0) {
        throw new Error(`${config.name} navigation URL patterns are not configured.`);
    }
    return [...patterns];
}

export const ipwConfigPcpartpicker: IpwConfig = {
    ipw_id: IpwSystemId.PCPARTPICKER,
    ipw_system_id: IpwSystemIdKey.PCPARTPICKER,
    name: 'PCPartPicker',
    urlPattern: 'https://pcpartpicker.com/*',
    workflow_prefix: 'pcpartpicker',
    allowedWebsites: [
        {
            type: MerchantWebsiteType.IPW,
            system_id: IpwSystemId.PCPARTPICKER,
            url: 'https://pcpartpicker.com/list',
            name: 'PCPartPicker (Demo SOR)',
            allowedUrlPatterns: ['https://pcpartpicker.com/'],
            sessionPersistence: 'persistent',
            promoteSessionCookies: false,
            urlPatternsRules: [],
            fetchDefaultAction: 'allow',
            iframeUrlPatterns: [],
            iframeDefaultAction: 'allow',
        },
    ],
    rpaModules: [RpaModuleName.PCPARTPICKER_IPW],
};

export const ipwConfigDealertrack: IpwConfig = {
    ipw_id: IpwSystemId.DEALERTRACK,
    ipw_system_id: IpwSystemIdKey.DEALERTRACK,
    name: 'Dealertrack',
    urlPattern: 'https://*.coxautoinc.com/*',
    workflow_prefix: 'dealertrack',
    allowedWebsites: [
        {
            type: MerchantWebsiteType.IPW,
            system_id: IpwSystemId.DEALERTRACK,
            url: 'https://signin.coxautoinc.com/',
            name: 'Dealertrack',
            allowedUrlPatterns: ['https://*.coxautoinc.com/*'],
            sessionPersistence: 'persistent',
            promoteSessionCookies: true,
            urlPatternsRules: [],
            fetchDefaultAction: 'allow',
            iframeUrlPatterns: [],
            iframeDefaultAction: 'allow',
        },
    ],
    rpaModules: [RpaModuleName.DEALERTRACK_IPW],
    reconciliation: {
        paymentCode: 'AB',
        doInjectApprovalNumber: false,
        doFinalizeReconciliation: false,
    },
};

export const defaultIpgConfigByKey: Record<IpgSystemIdKey, IpgConfig> = {
    [IpgSystemIdKey.VALOR_US]: ipgConfigValorProd,
};

export const defaultIpwConfigByKey: Record<IpwSystemIdKey, IpwConfig> = {
    [IpwSystemIdKey.PCPARTPICKER]: ipwConfigPcpartpicker,
    [IpwSystemIdKey.DEALERTRACK]: ipwConfigDealertrack,
};

export const defaultIpgConfigById: Record<IpgSystemId, IpgConfig> = {
    [IpgSystemId.VALOR_US]: ipgConfigValorProd,
};

export const defaultIpwConfigById: Record<IpwSystemId, IpwConfig> = {
    [IpwSystemId.PCPARTPICKER]: ipwConfigPcpartpicker,
    [IpwSystemId.DEALERTRACK]: ipwConfigDealertrack,
};

export const defaultIntegratedWebsiteSystemIds = [
    IpgSystemId.VALOR_US,
    IpwSystemId.DEALERTRACK,
    IpwSystemId.PCPARTPICKER,
] as const satisfies readonly ThirdPartySystemId[];

export const defaultIntegratedWebsiteDisplayNameBySystemId: Partial<
    Record<ThirdPartySystemId, string>
> = {
    [IpgSystemId.VALOR_US]: ipgConfigValorProd.name,
    [IpwSystemId.DEALERTRACK]: ipwConfigDealertrack.name,
    [IpwSystemId.PCPARTPICKER]: ipwConfigPcpartpicker.name,
};

export const defaultIntegratedWebsiteLaunchUrlBySystemId: Partial<
    Record<ThirdPartySystemId, string>
> = {
    [IpgSystemId.VALOR_US]: requireDefaultLaunchUrl(ipgConfigValorProd),
    [IpwSystemId.DEALERTRACK]: requireDefaultLaunchUrl(ipwConfigDealertrack),
    [IpwSystemId.PCPARTPICKER]: requireDefaultLaunchUrl(ipwConfigPcpartpicker),
};

export const defaultIntegratedWebsiteAllowedWebsiteBySystemId: Partial<
    Record<ThirdPartySystemId, AllowedWebsite>
> = {
    [IpgSystemId.VALOR_US]: requireDefaultAllowedWebsite(ipgConfigValorProd),
    [IpwSystemId.DEALERTRACK]:
        requireDefaultAllowedWebsite(ipwConfigDealertrack),
    [IpwSystemId.PCPARTPICKER]:
        requireDefaultAllowedWebsite(ipwConfigPcpartpicker),
};

export const defaultIntegratedWebsiteAllowedUrlPatternsBySystemId: Partial<
    Record<ThirdPartySystemId, readonly string[]>
> = {
    [IpgSystemId.VALOR_US]:
        requireDefaultNavigationAllowedUrlPatterns(ipgConfigValorProd),
    [IpwSystemId.DEALERTRACK]:
        requireDefaultNavigationAllowedUrlPatterns(ipwConfigDealertrack),
    [IpwSystemId.PCPARTPICKER]:
        requireDefaultNavigationAllowedUrlPatterns(ipwConfigPcpartpicker),
};

export const defaultIpxCredentialLaunchUrlBySystemId =
    defaultIntegratedWebsiteLaunchUrlBySystemId;

export const defaultMerchantConfig: MerchantConfig = MerchantConfigSchema.parse({
    ipg: defaultIpgConfigByKey,
    ipw: defaultIpwConfigByKey,
});
